import {hasNoImage} from '../../../components/common/ProductThumb'

/**
 * The tiles of the image sorter and the `image_order` they are sent as.
 *
 * A tile is either a File the supplier just picked, or an image the server already has:
 *   {image, is_main: true}            the stored main image
 *   {image, saved_index}              a gallery image of a request, by its stored position
 *   {image, saved_index, product_image_id}   a gallery row of a live product
 * The first tile is always the main image.
 */

type VariationImages = {image?: {value: any}; product_image?: any[]}

/** The File behind a tile, when it is one the supplier just picked. */
export const fileOf = (item: any): File | undefined => {
  const file = item instanceof File ? item : item?.file
  return file instanceof File ? file : undefined
}

const storedTile = (item: any, position: number) =>
  typeof item === 'string' ? {image: item, saved_index: position} : {...item, saved_index: item.saved_index ?? position}

/**
 * The tiles of a stored product or request: its main image first, then its gallery.
 * Many live products repeat their main image as a gallery row; it is shown once, as the
 * main image, so the first tile really is the main image and an untouched save changes nothing.
 */
export function productImageList(mainImage: string | null | undefined, gallery: any[] | null | undefined): any[] {
  const tiles = (gallery || []).map(storedTile)
  if (hasNoImage(mainImage)) return tiles
  return [{image: mainImage, is_main: true}, ...tiles.filter((tile) => tile.image !== mainImage)]
}

/**
 * A variation's tiles. Until the supplier arranges them the state holds the main image and the
 * gallery apart, as the server sent them; afterwards it holds the arranged list itself.
 */
export function variationImageList(variation: VariationImages | undefined): any[] {
  const main = variation?.image?.value
  const stored = variation?.product_image || []
  const arranged =
    stored.some((item) => item?.is_main) ||
    (main instanceof File && stored[0] === main) ||
    (stored[0]?.saved_index !== undefined && stored[0]?.image === main)
  if (arranged) return stored

  const tiles = stored.map((item, position) => (item instanceof File ? item : storedTile(item, position)))
  if (!main) return tiles
  return [
    main instanceof File ? main : {image: main, is_main: true},
    ...tiles.filter((tile) => tile instanceof File || tile.image !== main),
  ]
}

function eachOrderToken(images: any[], addGalleryFile: (file: File) => void, addToken: (token: string) => void) {
  const mainIsNew = !!fileOf(images[0])
  let newIndex = 0
  images.forEach((item, position) => {
    const file = fileOf(item)
    if (file) {
      // The first image travels in the dedicated main-image field, never as a gallery file too.
      if (position === 0) return addToken('main')
      addGalleryFile(file)
      return addToken(`new:${newIndex++}`)
    }
    if (item?.product_image_id) return addToken(`existing:${item.product_image_id}`)
    if (item?.is_main) return addToken(mainIsNew ? 'previous-main' : 'main')
    if (item?.saved_index !== undefined) addToken(`saved:${item.saved_index}`)
  })
}

/** A product's gallery files and image order. Its main image file goes out as `image`, with the other fields. */
export function appendOrderedImages(formData: FormData, images: any[]): void {
  eachOrderToken(
    images,
    (file) => formData.append('product_image[]', file),
    (token) => formData.append('image_order[]', token)
  )
}

/** One variation's main image file, gallery files and image order. */
export function appendVariationImages(formData: FormData, variation: VariationImages, index: number): void {
  const images = variationImageList(variation)
  const mainFile = fileOf(images[0])
  if (mainFile) formData.append(`variations[${index}][image]`, mainFile)
  eachOrderToken(
    images,
    (file) => formData.append(`variations[${index}][product_image][]`, file),
    (token) => formData.append(`variations[${index}][image_order][]`, token)
  )
}
